import React, {useContext, useEffect, useState} from 'react'
import {Lang} from 'yougile-lang'
import {Time, Util} from 'yougile-util'
import {DeadlineStickerEntry, DeadlineStickerValue, Hub} from 'yougile-model'
import {Backend} from '@common/backend'
import {DropdownRelayoutTriggerContext} from '@components/common/dropdown/dropdown'
import {globals} from '@extensions/globals-base'
import {DeadlineCtl} from '@extensions/sticker/controllers/deadline-ctl'
import {
  DeadlineEditForm,
  DeadlineEditFormData
} from '@extensions/sticker/views-web/deadline/deadline-edit-form'
import {DeadlineBeforeEdit} from './deadline-before-edit'

const preferStartDateKey = 'deadline-prefer-start-date'
const isPreferredStartDate = () => !!Backend.loadLocal(preferStartDateKey)

const setPreferredStartDate = (value: boolean) => {
  if (value) {
    Backend.saveLocal(preferStartDateKey, '1')
  } else {
    Backend.removeLocal(preferStartDateKey)
  }
}

export const getDefaultEntry = (): DeadlineStickerEntry => ({
  deadline: undefined,
  timestamp: Time.now(),
  notifyBefore: 0,
  withTime: false,
  by: globals.currentUserId
})

export const getValue = (
  cur: DeadlineStickerEntry,
  hist: DeadlineStickerEntry[]
): DeadlineStickerValue => {
  const current: DeadlineStickerEntry = {
    ...cur,
    by: globals.currentUserId,
    timestamp: Time.now()
  }
  const history = hist ? [...hist, Util.clone(current)] : []
  const newVal = {current, history}
  return newVal
}

interface Props {
  val: DeadlineStickerValue
  setCurrentValue?: (value: DeadlineStickerValue | undefined) => void
  task?: Hub
  canEdit?: boolean
}

export const DeadlineEdit = (props: Props) => {
  const {val, setCurrentValue, task, canEdit} = props
  const current = val?.current
  const history = val?.history ?? []
  const [editing, setEditing] = React.useState(!current && !!canEdit)
  const triggerDropdownRelayout = useContext(DropdownRelayoutTriggerContext)
  const onEditModeActivate = () => {
    if (canEdit) {
      setEditing(true)
      triggerDropdownRelayout()
    }
  }
  const onEditFormUpdate = (newCurrent?: DeadlineStickerEntry) => {
    if (newCurrent) {
      setCurrentValue?.(getValue(newCurrent, history))
    }
    triggerDropdownRelayout()
  }
  const className = Util.className({
    'sticker-deadline-edit': true,
    editing
  })

  return (
    <div className={className}>
      {editing ? (
        <DeadlineEditStatefulForm
          entry={current ?? getDefaultEntry()}
          onUpdate={onEditFormUpdate}
          task={task}
          canEdit={canEdit}
        />
      ) : (
        <DeadlineBeforeEdit
          edit={onEditModeActivate}
          value={formatDate(current?.deadline, current?.withTime)}
          startValue={formatDate(current?.startDate)}
          history={history ?? []}
          canEdit={canEdit}
        />
      )}
    </div>
  )
}

export const DeadlineEditStatefulForm = React.memo(
  (props: {
    entry: DeadlineStickerEntry
    onUpdate: (entry?: DeadlineStickerEntry) => void
    task: Hub | undefined
    canEdit?: boolean
  }) => {
    const {entry, onUpdate, task, canEdit} = props
    const [formData, setFormData] = useState(() =>
      getInitialFormData(entry, task)
    )

    // If a server rejects date range, update form data
    useEffect(() => {
      setFormData(formData => {
        const newFormData = getInitialFormData(entry, task)
        if (
          newFormData.range.start?.getTime() ===
            formData.range.start?.getTime() &&
          newFormData.range.end?.getTime() === formData.range.end?.getTime()
        ) {
          return formData
        }
        return {...formData, range: newFormData.range}
      })
    }, [entry])

    const {fullRangeMode} = formData
    useEffect(() => setPreferredStartDate(fullRangeMode), [fullRangeMode])

    const blockedRangeSections = task ? DeadlineCtl.getBlockedPoints(task) : []

    const onChange = (data: DeadlineEditFormData) => {
      if (
        blockedRangeSections.includes('start') &&
        !data.range.start &&
        data.range.end
      ) {
        data.range.start =
          formData.range.end && formData.range.end <= data.range.end
            ? new Date(formData.range.end)
            : new Date(data.range.end)
        data.fullRangeMode = true
      }

      setFormData(data)
      onUpdate(getNextEntry(entry, data))
    }

    return (
      <DeadlineEditForm
        value={formData}
        onChange={onChange}
        task={task}
        blockedRangeSections={blockedRangeSections}
        canEdit={canEdit}
      />
    )
  }
)

const getInitialFormData = (
  entry: DeadlineStickerEntry,
  task: Hub | undefined
): DeadlineEditFormData => {
  const {startDate, deadline} = entry
  const range = {
    start: startDate
      ? Time.toLocalDate(Time.roundToMinutes(startDate))
      : undefined,
    end: deadline ? Time.toLocalDate(Time.roundToMinutes(deadline)) : undefined
  }
  const blockedPoints = task ? DeadlineCtl.getBlockedPoints(task) : []
  return {
    range,
    fullRangeMode:
      !!startDate || isPreferredStartDate() || blockedPoints.includes('start'),
    withTimeMode: entry.withTime ?? false,
    notifyBefore: entry.notifyBefore ?? 0
  }
}

const getNextEntry = (
  prevEntry: DeadlineStickerEntry,
  data: DeadlineEditFormData
): DeadlineStickerEntry | undefined => {
  const {start, end} = data.range
  if (!end) {
    return undefined
  }
  return {
    ...prevEntry,
    startDate: start && Time.toTimestamp(start, true),
    deadline: Time.toTimestamp(end, true),
    notifyBefore: data.notifyBefore,
    withTime: data.withTimeMode
  }
}

const formatDate = (timestamp: number | undefined, withTime?: boolean) => {
  if (!timestamp) {
    return ''
  }
  const timeString = withTime
    ? ` ${Lang.msg.at} ${Time.formatShort(timestamp, true)}`
    : ''
  return Time.formatDate(timestamp, {alwaysWithYear: true}) + timeString
}
